// VoiceFormContext — registro del FORMULARIO ACTIVO para el asistente de voz.
//
// Las pantallas (consulta, evolución, receta…) no exponen su contexto por la
// ruta (pestañas keep-alive + estado local), así que cada formulario se
// registra al montarse con useVoiceForm(...): declara su schema de campos (el
// contrato que se manda al gateway para que Claude estructure el dictado) y un
// aplicador que escribe los valores ACEPTADOS usando sus propios setters.
// Última en registrarse gana (pila); al desmontar se retira.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface CampoVoz {
  clave: string;
  etiqueta: string;
  tipo: 'texto' | 'textarea' | 'numero' | 'fecha' | 'select' | 'checkbox';
  opciones?: string[];
  unidad?: string;
}

/** Ítem de prescripción propuesto por el dictado (con candidatos del catálogo). */
export interface ItemRecetaVoz {
  producto_id: string | null;
  medicamento: string;
  comercial?: string;
  concentracion?: string;
  via?: string;
  forma?: string;
  cantidad?: string;
  indicaciones?: string;
}

export interface RegistroVoz {
  modulo: string;                    // 'consulta' | 'evolucion' | 'formato' | 'receta' | ...
  pacienteId?: string | null;
  campos: CampoVoz[];
  aplicar: (valores: Record<string, unknown>) => void;  // escribe en el formulario
  /** Solo la pantalla de receta: activa la extracción de prescripciones */
  receta?: { vias: string[]; formas: string[] };
  aplicarReceta?: (items: ItemRecetaVoz[]) => void;     // agrega filas al grid
}

interface VoiceFormCtx {
  activo: RegistroVoz | null;
  registrar: (r: RegistroVoz) => number;
  actualizar: (id: number, r: RegistroVoz) => void;
  retirar: (id: number) => void;
}

const Ctx = createContext<VoiceFormCtx | null>(null);
let seq = 0;

export function VoiceFormProvider({ children }: { children: ReactNode }) {
  const [pila, setPila] = useState<{ id: number; reg: RegistroVoz }[]>([]);

  const registrar = useCallback((reg: RegistroVoz) => {
    const id = ++seq;
    setPila((p) => [...p, { id, reg }]);
    return id;
  }, []);
  const actualizar = useCallback((id: number, reg: RegistroVoz) => {
    setPila((p) => p.map((e) => (e.id === id ? { id, reg } : e)));
  }, []);
  const retirar = useCallback((id: number) => {
    setPila((p) => p.filter((e) => e.id !== id));
  }, []);

  const value = useMemo<VoiceFormCtx>(() => ({
    activo: pila.length ? pila[pila.length - 1].reg : null,
    registrar, actualizar, retirar,
  }), [pila, registrar, actualizar, retirar]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** El orbe consume el formulario activo (null si ninguna pantalla registró). */
export function useVoiceFormActivo(): RegistroVoz | null {
  return useContext(Ctx)?.activo ?? null;
}

/** Las pantallas registran su formulario mientras están montadas.
 *  `campos` debe ser estable o memoizado; `aplicar` puede cambiar (va por ref). */
export function useVoiceForm(reg: RegistroVoz | null) {
  const ctx = useContext(Ctx);
  const idRef = useRef<number | null>(null);
  const aplicarRef = useRef(reg?.aplicar);
  aplicarRef.current = reg?.aplicar;
  const aplicarRecetaRef = useRef(reg?.aplicarReceta);
  aplicarRecetaRef.current = reg?.aplicarReceta;

  // clave de identidad: si cambia el schema/paciente/módulo se re-registra
  const firma = reg ? JSON.stringify({ m: reg.modulo, p: reg.pacienteId, c: reg.campos, r: !!reg.receta }) : '';

  useEffect(() => {
    if (!ctx || !reg) return;
    const estable: RegistroVoz = {
      modulo: reg.modulo,
      pacienteId: reg.pacienteId,
      campos: reg.campos,
      aplicar: (v) => aplicarRef.current?.(v),
      receta: reg.receta,
      aplicarReceta: reg.aplicarReceta ? (items) => aplicarRecetaRef.current?.(items) : undefined,
    };
    if (idRef.current == null) {
      idRef.current = ctx.registrar(estable);
    } else {
      ctx.actualizar(idRef.current, estable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, !!ctx]);

  useEffect(() => () => {
    if (ctx && idRef.current != null) { ctx.retirar(idRef.current); idRef.current = null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
